import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd, Event, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from './services/theme.service';
import { TutorialService } from './services/tutorial.service';
import { TenantService } from './services/tenant.service';
import { UsersService } from './services/users.service';

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  isSidebarCollapsed = false;
  isSidebarMobileOpen = false;
  isLoginPage = false;
  currentLanguage = 'pt';
  footerLogo = 'assets/img/di2win_logo_dark.png';
  private tutorialCheckedForSession = false;
  private preferencesSyncedForSession = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private themeService: ThemeService,
    private tutorialService: TutorialService,
    private tenantService: TenantService,
    private usersService: UsersService
  ) {
    this.router.events.pipe(
      filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.isLoginPage = this.isOnLoginPage(event.urlAfterRedirects || event.url);
      if (this.isLoginPage) {
        this.tutorialCheckedForSession = false;
        this.preferencesSyncedForSession = false;
      }
      this.tryInitTutorial();
      this.trySyncPreferences();
    });
  }

  ngOnInit(): void {
    this.initTranslation();
    this.isLoginPage = this.isOnLoginPage(this.router.url);

    this.updateFooterLogo(this.themeService.currentTheme);
    this.themeService.theme$.subscribe(theme => this.updateFooterLogo(theme));

    this.tenantService.loadTenants().subscribe(() => {
      const slug = this.extractSlugFromUrl(this.router.url);
      if (slug && this.tenantService.isValidSlug(slug)) {
        this.tenantService.resolve(slug).subscribe();
      } else {
        this.tenantService.setDefault();
      }
    });

    this.tryInitTutorial();
    this.trySyncPreferences();

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.isSidebarMobileOpen = false;
      }
    });
  }

  private tryInitTutorial(): void {
    if (this.tutorialCheckedForSession) {
      return;
    }

    if (!this.isLoginPage && sessionStorage.getItem('token')) {
      this.tutorialService.initAutoStart();
      this.tutorialCheckedForSession = true;
    }
  }

  private initTranslation(): void {
    this.translate.addLangs(['pt', 'en', 'es']);
    
    this.translate.setDefaultLang('pt');
    
    const savedLanguage = sessionStorage.getItem('selectedLanguage');

    if (savedLanguage && this.translate.getLangs().includes(savedLanguage)) {
      this.currentLanguage = savedLanguage;
    } else {
      const browserLang = this.translate.getBrowserLang();
      this.currentLanguage = (browserLang && ['pt', 'en', 'es'].includes(browserLang)) ? browserLang : 'pt';
    }

    this.translate.use(this.currentLanguage);
  }

  private trySyncPreferences(): void {
    if (this.preferencesSyncedForSession) return;
    if (this.isLoginPage || !sessionStorage.getItem('token')) return;

    this.preferencesSyncedForSession = true;
    this.usersService.getMe().subscribe({
      next: (response) => {
        const preferences = response.user?.preferences;
        if (!preferences) return;

        if (preferences.theme) {
          this.themeService.setTheme(preferences.theme);
        }
        if (preferences.ui_language && this.translate.getLangs().includes(preferences.ui_language)) {
          this.changeLanguage(preferences.ui_language);
        }
      },
      error: () => { this.preferencesSyncedForSession = false; }
    });
  }

  changeLanguage(language: string): void {
    this.translate.use(language);
    this.currentLanguage = language;
    sessionStorage.setItem('selectedLanguage', language);
  }

  onSidebarCollapsed(collapsed: boolean): void {
    this.isSidebarCollapsed = collapsed;
  }

  toggleMobileSidebar() {
    this.isSidebarMobileOpen = !this.isSidebarMobileOpen;
  }

  closeMobileSidebar() {
    this.isSidebarMobileOpen = false;
  }

  private updateFooterLogo(theme: string): void {
    this.footerLogo = theme === 'dark' ? 'assets/img/di2win_logo_white.png' : 'assets/img/di2win_logo_dark.png';
  }

  private isOnLoginPage(url: string): boolean {
    if (!url) return false;
    const path = url.split('?')[0].split('#')[0].split(';')[0].replace(/\/$/, '');
    return path === '/login' || path.endsWith('/login');
  }

  private extractSlugFromUrl(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(s => s.length > 0);
    
    if (segments.length > 0 && this.tenantService.isValidSlug(segments[0])) {
      return segments[0];
    }
    
    return '';
  }
}